import React, { useEffect, useState } from "react";
import { Button, Select, Switch, Table, Tooltip } from "antd";
import { useSearchParams } from "react-router";
import ResizableDrawer from "@/components/UI/ResizableDrawer";
import { useScopeParams } from "@/hooks/useScopeParams";
import { CheckOutlined, CloseOutlined, MinusOutlined } from "@ant-design/icons";

import AlertIcon from "../../../assets/icons/alert_icon.svg?react";
import CalendarMonthIcon from "../../../assets/icons/calendar_month.svg?react";
import { getColumnConfig } from "./VAColumnConfig";
import { drawerTableColumns } from "./DrawerColumConfig";

import {
  getActivitiesOptions,
  getGroupArray,
  getLastKeyValue,
  toTitleCase,
} from "../../../utils/tableConfigs/ActivitiesTableUtilFunctions";

import InfoTooltip from "../../../components/UI/InfoTooltip";
import GuideButton from "../../../components/UI/GuideButton";
import TooltipTag from "../../../components/UI/TooltipTag";
import {
  pillColor,
  textColor,
  tooltipTitle,
} from "../../../utils/tableConfigs/VAStatusMapping";
import { UNSCHEDULED_VISIT_PREFIX } from "../../../utils/unscheduledVisitUtils/unscheduledVisitUtils";
import Error from "../../../components/UI/Error";
import Error403 from "../../../components/UI/Error403";
import { useFetch } from "../../../hooks/FetchHook";
import Loader from "../../../components/UI/Loader";
import { normalizeText } from "@/utils/tableConfigs/GlobalFunctions";
const tableLegend = [
  {
    title: "All Activities Completed",
    icon: (
      <CheckOutlined
        style={{
          color: "#06709B",
          backgroundColor: "#D2EFF9",
          padding: "2px",
        }}
      />
    ),
    tooltip: "All expected activities were completed at the visit timepoint.",
  },
  {
    title: "Some Activities Completed",
    icon: (
      <div className="flex items-center justify-center bg-[#FAEEDF]">
        <AlertIcon />
      </div>
    ),
    tooltip:
      "Some, but not all, expected activities were completed at a visit timepoint.",
  },
  {
    title: "No Activities Completed",
    icon: (
      <CloseOutlined
        style={{
          color: "#73134A",
          backgroundColor: "#EBD1E0",
          padding: "2px",
        }}
      />
    ),
    tooltip: "No activities were completed at this time point",
  },
  {
    title: "No Data",
    icon: (
      <MinusOutlined
        style={{
          color: "#D5DADC",
          backgroundColor: "white",
          border: "1px solid #D5DADC",
          padding: "2px",
          borderRadius: "2px",
        }}
      />
    ),
    tooltip: "No data or activity not required.",
  },
];

export default function SubjectVisitsActivities() {
  const { id } = useScopeParams();
  const path = `/${id}/subjects/overview`;

  // Optional deep-link from the Study Design "expected schedule" (S4): pre-filter to a
  // specific activity. Falls back to "all" when absent.
  const [searchParams] = useSearchParams();
  const [selectedActivities, setSelectedActivities] = useState(
    searchParams.get("activity") || "all",
  );

  const {
    data: countData,
    isLoading: loadingCount,
    isError: isErrorCount,
    error: errorObjCount,
  } = useFetch({
    endpointSuffix: `activities/by-count-completion/${
      selectedActivities === "all" ? "" : selectedActivities
    }`,
  });

  const {
    data: activityDictionary,
    isError: isErrorActivity,
    error: errorObjActivity,
  } = useFetch({ endpointSuffix: "activities/" });

  const isError = isErrorCount || isErrorActivity;
  const errorObj = errorObjCount || errorObjActivity;

  const [selectedCell, setSelectedCell] = useState<any>(null);
  const [showCount, setShowCount] = useState(false);
  const [tableHeight, setTableHeight] = useState(window.innerHeight - 400);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [columnToHide, setColumnToHide] = useState(UNSCHEDULED_VISIT_PREFIX);
  const [tableKey, setTableKey] = useState(0);

  // Study Status opens pre-filtered to enrolled subjects; "Clear all filters" turns
  // that off for the rest of the visit, so clearing really shows everyone.
  const [applyDefaultFilters, setApplyDefaultFilters] = useState(true);

  // antd reads a column's `defaultFilteredValue` only when the Table mounts, and the
  // columns stay empty until the fetch lands — so key the Table on whether rows exist
  // to remount it once the Study Status filter options are actually there.
  const hasRows = (countData as any)?.length > 0;

  const customOrder = [
    "VISIT",
    "SCREENING",
    "INTERVENTION",
    "PHYSICAL EVALUATION",
    "LABS",
    "IMAGING",
    "QUESTIONNAIRE",
  ];

  //Get the keys of the activity dictionary in custom order
  const activityCategoryList = Array.isArray(activityDictionary)
    ? activityDictionary
        .map((obj) => Object.keys(obj)[0])
        .sort((a, b) => {
          const indexA = customOrder.indexOf(a);
          const indexB = customOrder.indexOf(b);

          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        })
    : [];

  //Sort the activity dictionary
  const sortedActivityDictionary = sortActivityDictionary(activityDictionary);

  //Create options for select dropdown
  const optionsActivities = getActivitiesOptions(sortedActivityDictionary);

  // Event handler for table cell click
  const handleCellClick = (columnName: any, value: any, record: any) => {
    const cellInfo = {
      subject: record.subject,
      site: record.site,
      status: record.study_status,
      [columnName]: value,
    };

    setSelectedCell(cellInfo);
    setOpenDrawer(true);
  };

  //Get the last key value pair of the selected cell that corresponds to the visit week
  const visitType = getLastKeyValue(selectedCell ? selectedCell : {});

  //Get the columns for the each activity table
  const countColumns = (type: string) =>
    (countData as any)?.length > 0
      ? getColumnConfig(
          countData as any,
          handleCellClick,
          columnToHide,
          path,
          type,
          id,
          applyDefaultFilters,
        )
      : [];

  useEffect(() => {
    const handleResize = () => {
      setTableHeight(window.innerHeight - 390);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  //Event handler for the Select component that contains the list of activities
  const handleSelectActivities = async (selected: any) => {
    setSelectedActivities(selected);
    setTableKey((key) => key + 1);
  };

  //Event handler for the Switch component that switches between count and completion tables
  const onChangeDataSwitch = (checked: any) => {
    setShowCount(checked);
  };

  const onChangeColumnSwitch = (checked: any) => {
    setShowUnscheduled(checked);
    setColumnToHide(checked ? "" : UNSCHEDULED_VISIT_PREFIX);
  };

  //Event handler for the "Clear all" button
  const handleClearAll = () => {
    setSelectedActivities("all");
    setApplyDefaultFilters(false);
    setTableKey((key) => key + 1);
  };

  if (isError) {
    if ((errorObj as any)?.statusCode === 403) {
      return (
        <div className="flex mt-25 justify-center items-center">
          <Error403 />
        </div>
      );
    }
    return <Error />;
  }

  return (
    <div className="p-4">
      <div className="flex mt-4 mb-8 items-center justify-between">
        <div className="flex gap-2 items-center">
          <h1 className="text-[18px] font-bold">
            Subjects: Visits & Activities Overview
          </h1>
          <InfoTooltip
            data-testid="subjects-visits-activities-page-tooltip"
            title="View the study schedule of activities, activity completion statuses and activity counts per subject, per timepoint. Click the cell for detailed information on that visit. Use the toggle to view unscheduled visits."
          />
        </div>
        <div className="flex gap-3 items-center">
          <GuideButton pageName="/wiki/guides/exploration-subjects-visits-activities" />
        </div>
      </div>

      <div className="flex justify-between items-center mt-3 mb-6">
        <div className="flex gap-4">
          <Tooltip title="Select a single activity by which to filter the table.">
            <Select
              data-testid="subjects-visits-activities-activity-select"
              className="customSelect shadow-xs"
              value={selectedActivities}
              onChange={handleSelectActivities}
              placeholder="Activity: ALL"
              options={optionsActivities}
              style={{
                width: 300,
                marginRight: "10px",
              }}
              labelRender={({ label }) => <span>Activity: {label}</span>}
            />
          </Tooltip>
          <Button type="text" icon={<CloseOutlined />} onClick={handleClearAll}>
            Clear all filters
          </Button>
        </div>
        <div className="flex">
          <div className="flex gap-3 items-center">
            <p className={showCount ? "text-[#556C75]" : "text-[black]"}>
              Show completion
            </p>
            <Tooltip title="Show high level completion of all activities at a visit or a break down of activity counts per visit.">
              <Switch
                data-testid="subjects-visits-activities-show-count-switch"
                onChange={onChangeDataSwitch}
                value={showCount}
              />
            </Tooltip>
            <p className={showCount ? "text-[black]" : "text-[#556C75]"}>
              Show count
            </p>
          </div>
          <p className="text-[#556C75] mr-3 ml-3">|</p>
          <div className="flex items-center gap-3">
            <p className={showUnscheduled ? "text-[black]" : "text-[#556C75]"}>
              Show Unscheduled Visits
            </p>
            <Switch checked={showUnscheduled} onChange={onChangeColumnSwitch} />
          </div>
        </div>
      </div>

      <div className="flex bg-white items-center p-2 gap-4 rounded-sm mb-2 shadow-xs">
        {tableLegend.map((legend, index) => (
          <Tooltip title={legend.tooltip} key={legend.title}>
            <div
              className="flex gap-3 items-center"
              data-testid={`subjects-visits-activities-legend-${index}`}
            >
              {legend.icon}
              <p>{legend.title}</p>
            </div>
          </Tooltip>
        ))}
      </div>
      <Table
        key={`${tableKey}-${hasRows ? "rows" : "empty"}`}
        bordered
        className="shadow-md"
        columns={countColumns(showCount ? "count" : "completion") as any}
        dataSource={countData as any}
        loading={{
          indicator: (
            <div className="flex justify-center w-full h-full items-center">
              <Loader noMargin tableLoader />
            </div>
          ),
          spinning: loadingCount,
        }}
        rowKey={(record) => record.participant_id as string}
        scroll={{
          x: 1500,
          y: tableHeight,
        }}
        size="small"
        pagination={false}
        virtual={true}
        footer={(displayData) => (
          <div className="flex gap-4">
            <p className="font-bold" data-testid="table-total-records">
              Total records: {(countData as any)?.length}
            </p>
            {displayData?.length !== (countData as any)?.length ? (
              <p className="font-bold" data-testid="table-selected-records">
                Selected records: {displayData?.length}
              </p>
            ) : (
              ""
            )}
          </div>
        )}
      />
      <SubjectDrawer
        selectedCell={selectedCell}
        visitType={visitType}
        activityCategoryList={activityCategoryList}
        drawerOpen={openDrawer}
        setDrawerOpen={setOpenDrawer}
      />
    </div>
  );
}

const SubjectDrawer = ({
  selectedCell,
  visitType,
  activityCategoryList,
  drawerOpen,
  setDrawerOpen,
}: any) => {
  const endcodeVisitType = encodeURIComponent(visitType.key);

  const { data, isLoading, isError } = useFetch({
    endpointSuffix: `activities/${selectedCell?.subject}/?timepoint=${endcodeVisitType}`,
    useQueryOptions: {
      enabled: drawerOpen,
    } as any,
  });

  const subjectByActivityCategory = getGroupArray(
    data as any,
    activityCategoryList,
    "activity_type",
  );
  const visitDate = subjectByActivityCategory?.map((category) => {
    const title = Object.keys(category)[0];
    const data = category[title];
    return title === "VISIT" ? data[0]?.date : "";
  });

  const allRequired = subjectByActivityCategory?.filter((category) => {
    const title = Object.keys(category)[0];
    const data = category[title];
    // Filter out categories with empty data arrays or all "Not Required" statuses
    return (
      data &&
      data.length > 0 &&
      data.some((item) => item.activity_status !== "Not Required")
    );
  });

  const allNotRequired = subjectByActivityCategory?.every((category) => {
    const title = Object.keys(category)[0];
    const data = category[title];
    // Check if all items in the data array have the ACTIVITY_STATUS of "Not Required"
    return data.every((item) => item.activity_status === "Not Required");
  });

  const studyStatus = normalizeText(selectedCell?.status) || "-";
  const headersArray = [
    { key: "SITE", value: selectedCell?.site || "-" },
    { key: "TIMEPOINT", value: visitType.key || "-" },
    { key: "DATE", value: visitDate || "" },
  ];
  return (
    <ResizableDrawer
      headerTitle={
        <div className="flex items-center gap-2">
          <h1 className="text-white">Subject {selectedCell?.subject}</h1>
          <TooltipTag
            data-testid="drawer-header-study-status"
            tagColor={pillColor[studyStatus]}
            textColor={textColor[studyStatus]}
            tooltipTitle={tooltipTitle[studyStatus]}
            tagStyle={{
              padding: "5px 20px",
              borderRadius: "6px",
              fontSize: "14px",
            }}
          >
            {studyStatus}
          </TooltipTag>
        </div>
      }
      headersArray={headersArray}
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sizeToggle={{
        collapsedWidth: 750,
        expandedWidth: 1250,
        initialWidth: 750,
      }}
      destroyOnHidden={true}
      closable={false}
      closeIcon={null}
    >
      <>
        {isError ? (
          <Error />
        ) : isLoading ? (
          <div className="flex mt-5 justify-center items-center">
            <Loader />
          </div>
        ) : (
          <>
            {allRequired.map((category) => {
              const title = Object.keys(category)[0];
              const data = category[title];
              return (
                <div key={title} className="mb-7">
                  <h1 className="font-bold mb-2">{toTitleCase(title)}</h1>
                  <Table
                    columns={drawerTableColumns as any}
                    rowKey={(record: any) => record.activity}
                    dataSource={data}
                    pagination={false}
                  />
                </div>
              );
            })}
            {allNotRequired && (
              <div className="flex flex-col items-center justify-center mt-5 mb-3">
                <CalendarMonthIcon />
                <p className="text-[#6e6c6c]">Visit Not Required</p>
              </div>
            )}
          </>
        )}
      </>
    </ResizableDrawer>
  );
};

//Function that sorts the activity dictionary in alphabetic order
const sortActivityDictionary = (activityDictionary: any) => {
  if (!activityDictionary || !Array.isArray(activityDictionary)) {
    return [];
  }
  // Extract key-value pairs and sort by keys
  const sortedDictionary = activityDictionary
    .map((obj) => {
      const key = Object.keys(obj)[0];
      const values = obj[key].sort(); // Sort values alphabetically
      return { [key]: values };
    })
    .sort((a, b) => {
      const keyA = Object.keys(a)[0].toUpperCase();
      const keyB = Object.keys(b)[0].toUpperCase();
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      return 0;
    });

  return sortedDictionary;
};
